/**
 * The site registry: every surveyed site `sites.json` lists, and the open
 * body's own rows. Module-level so it outlives a dispose of the Atlas.
 */

import { devFlag, urlFlag, TEST_SWITCHES } from '../urlFlags.ts';
import { dtmUrl } from './dtmBase.ts';

// Surveyed ground. Each site is a cluster of adjoining plots on one continuous
// HiRISE height field, every sample measured, with a per-plot detail tile
// fetched on descent. The plot radius is held at 1.2 km across all sites: a
// plot is a unit, and what changes between sites is how many of them the
// terrain will give you.
export interface Site {
  id: string;
  /** Which body's ground this is; `bake.body` on the bake side. */
  body: string;
  name: string;
  quad: string;
  blurb: string;
  /** Region centre on the body, degrees; the descent is aimed here. */
  center_lat_deg: number;
  center_lon_deg: number;
  plots: number;
  plot_area_km2: number;
  relief_m: number;
  region_mpp: number;
  tile_mpp: number | null;
  instrument: string;
  /** Not every row carries it; the bake's own patch half-size, km. */
  bake?: { radius_km?: number };
  /** A row without one reads as a landing site if it names a mission, else a named feature. */
  kind?: SiteKind;
  /** Other names the ground goes by: the crater, the mission, the landing point. */
  aliases?: string[];
  /** The source DTM's own product id. */
  product_id?: string;
  /** The baked window, degrees, east longitude 0-360. */
  footprint?: { lat_min_deg: number; lat_max_deg: number; lon_min_deg: number; lon_max_deg: number };
  landing_mission?: string;
}

export type SiteKind = 'landing' | 'feature' | 'canon';

// Populated from DTM_BASE/sites.json at mount, so adding a site is a bake plus
// a registry entry rather than an engine change.
let SITES: Site[] = [];
/** Whether the site registry fetch has landed, either way. */
let sitesLoaded = false;
/** The open body's own sites, in registry order; `siteIdx` indexes THIS. */
let bodySites: Site[] = [];
let siteBodyId: string | null = null;
let siteIdx = 0;

/** `?synthsites=<n>` stands in generated rows for reading the site panel
 * at scale. Test-switch only like `?rockshare=0` in src/scene/asteroids.ts: this
 * folds to dead code in a production build. */
function syntheticSiteCount(): number {
  if (!TEST_SWITCHES || typeof location === 'undefined') return 0;
  const n = Number(new URLSearchParams(location.search).get('synthsites'));
  return Number.isFinite(n) && n >= 1 ? Math.min(Math.floor(n), 5000) : 0;
}

/** `?zoomnofloor=1` puts back the descent with no eye floor under it and with
 * the focus point gliding onto new ground rather than acquiring it - the state
 * the wheel went through Tharsis in. DEV-only and folds out of a build with the
 * gate, the way `?synthsites=` above does. */
const zoomFloorOff = devFlag(() => TEST_SWITCHES && urlFlag('zoomnofloor'));

const SYNTH_PLACES = [
  'Hadley', 'Marius', 'Aristarchus', 'Copernicus', 'Fra Mauro', 'Taurus', 'Schroter',
  'Plato', 'Tycho', 'Alphonsus', 'Gassendi', 'Posidonius', 'Rumker', 'Ina', 'Sulpicius Gallus',
];
const SYNTH_FEATURES = [
  'Rille', 'Dome Field', 'Rim', 'Massif', 'Scarp', 'Terrace', 'Central Peak',
  'Ejecta Ray', 'Wrinkle Ridge', 'Pyroclastic Mantle',
];

/** n rows for one body: the first is the real one untouched, the rest a
 * golden-angle spiral filling a disk over the template's own baked patch, so
 * the walkaround mesh under a generated row's centre is ground the bake
 * covers rather than wherever a global spread happens to land. `bodyRadiusM`
 * is the body's own row from `bodies.json` (`rowById(id)?.radius_m`), used
 * only to turn the patch's km radius into a lat/lon offset; every 25th row is
 * long-form. Only name/blurb/quad/centre differ from the template. */
function syntheticSites(template: Site, n: number, bodyRadiusM: number): Site[] {
  const out: Site[] = [template];
  // A row near the hex's own corner can fall outside its measured tiles, so
  // generated centres stay inside 70% of the patch's own radius.
  const patchRadiusKm = template.bake?.radius_km
    ?? Math.sqrt(template.plot_area_km2 / (1.5 * Math.sqrt(3)));
  const safeRadiusKm = patchRadiusKm * 0.7;
  const bodyRadiusKm = bodyRadiusM / 1000;
  const cosLat = Math.max(0.05, Math.cos((template.center_lat_deg * Math.PI) / 180));
  for (let i = 1; i < n; i++) {
    const place = SYNTH_PLACES[i % SYNTH_PLACES.length] ?? 'Hadley';
    const feature = SYNTH_FEATURES[Math.floor(i / SYNTH_PLACES.length) % SYNTH_FEATURES.length] ?? 'Rille';
    // Sunflower spiral: golden-angle bearing, radius growing as sqrt(i/n) so
    // the rows fill the disk's area evenly rather than crowding its centre.
    const angleRad = ((i * 137.507) % 360) * (Math.PI / 180);
    const rKm = safeRadiusKm * Math.sqrt(i / n);
    const dLatDeg = ((rKm * Math.cos(angleRad)) / bodyRadiusKm) * (180 / Math.PI);
    const dLonDeg = ((rKm * Math.sin(angleRad)) / (bodyRadiusKm * cosLat)) * (180 / Math.PI);
    // A fabricated quad, not the real one, so the band heading the chip list
    // groups by still varies even though the centre no longer does. Bucketed
    // by i rather than cycled like place/feature above, so the bands land in
    // registry order and the highest index is still the last chip in the DOM.
    const bandIdx = Math.min(SYNTH_PLACES.length - 1, Math.floor((i * SYNTH_PLACES.length) / n));
    const band = SYNTH_PLACES[bandIdx] ?? 'Hadley';
    out.push({
      ...template,
      name: i % 25 === 0
        ? `Apollo 17 Taurus-Littrow valley, NAC pair M${104311715 + i}`
        : `${place} ${feature} ${i}`,
      blurb: `Generated row ${i}, standing in for a bake that has not happened.`,
      quad: band.toUpperCase(),
      center_lat_deg: template.center_lat_deg + dLatDeg,
      center_lon_deg: template.center_lon_deg + dLonDeg,
    });
  }
  return out;
}

/** `&synthspread=1` beside `?synthsites=<n>`: the site finder's DEV fixture; its rules are in tools/preview/site-finder-check.mjs. */
function syntheticSpread(): boolean {
  if (!TEST_SWITCHES || typeof location === 'undefined') return false;
  return new URLSearchParams(location.search).get('synthspread') === '1';
}

const SPREAD_KINDS: readonly SiteKind[] = ['feature', 'landing', 'canon'];
const SPREAD_SOURCES: Record<string, readonly string[]> = {
  mars: ['HiRISE (MRO)', 'CTX (MRO)', 'HRSC (Mars Express)'],
  luna: ['LROC NAC (LRO)', 'Kaguya TC (SELENE)', 'LROC WAC (LRO)'],
};
const SPREAD_PLACES: Record<string, readonly string[]> = {
  mars: ['Arabia', 'Candor', 'Eos', 'Hellas', 'Isidis', 'Mawrth', 'Nili', 'Olympus', 'Sabaea', 'Utopia', 'Xanthe'],
  luna: SYNTH_PLACES,
};

function spreadSyntheticSites(real: readonly Site[], n: number, bodyId: string): Site[] {
  const template = real[0];
  if (!template) return [];
  const out: Site[] = [];
  const sources = SPREAD_SOURCES[bodyId] ?? [template.instrument];
  const places = SPREAD_PLACES[bodyId] ?? SYNTH_PLACES;
  for (let i = 0; i < n; i++) {
    const lat = (Math.asin(1 - (2 * (i + 0.5)) / n) * 180) / Math.PI;
    const lon = (i * 137.50776405) % 360;
    const kind = SPREAD_KINDS[i % 3] ?? 'feature';
    const place = places[i % places.length] ?? 'Hadley';
    const feature = SYNTH_FEATURES[Math.floor(i / places.length) % SYNTH_FEATURES.length] ?? 'Rille';
    const halfDeg = 0.05 + (i % 7) * 0.03;
    out.push({
      ...template,
      id: `synth-${i}`,
      name: `${place} ${feature} ${i}`,
      blurb: `Generated row ${i}, standing in for a bake that has not happened.`,
      quad: '',
      center_lat_deg: lat,
      center_lon_deg: lon,
      instrument: sources[Math.floor(i / 3) % sources.length] ?? template.instrument,
      kind,
      aliases: kind === 'landing' ? [`Lander ${i} ellipse`] : [],
      product_id: `SYN_${String(i).padStart(5, '0')}`,
      landing_mission: kind === 'landing' ? `Lander ${i}` : undefined,
      footprint: {
        lat_min_deg: lat - halfDeg, lat_max_deg: lat + halfDeg,
        lon_min_deg: lon - halfDeg, lon_max_deg: lon + halfDeg,
      },
    });
  }
  return out;
}

/** Above this many rows the site panel gains a filter box and bands. */
const SITE_LIST_BANDS_ABOVE = 12;

/** The band a site is filed under: its quadrangle where the registry
 * names one, else its latitude band, read off the descent's own centre. */
function siteBandOf(st: Site): string {
  const quad = st.quad.trim();
  if (quad !== '') return quad.toUpperCase();
  const lat = st.center_lat_deg;
  if (lat >= 30) return 'NORTH OF 30 N';
  if (lat <= -30) return 'SOUTH OF 30 S';
  return 'WITHIN 30 DEGREES OF THE EQUATOR';
}

/** Folds case, hyphens and whitespace runs flat, so "Mare-Imbrium" and a
 * typed "mare  imbrium" match the same rows either direction. */
function normSiteQuery(s: string): string {
  return s.toLowerCase().replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
}

/** The site a body opens on; bake_region.py re-sorts sites.json by id, so file
 * order cannot say it. A body not listed opens on its first row. */
const DEFAULT_SITE: Readonly<Record<string, string>> = { luna: 'apollo15-hadley', mars: 'jezero-delta' };

/** Index into one body's rows of the site it opens on. */
function defaultSiteIdx(rows: readonly Site[], body: string | null): number {
  return Math.max(0, rows.findIndex((s) => s.id === DEFAULT_SITE[body ?? '']));
}

/**
 * Re-derive the open body's site list.
 *
 * A site is a patch of one body's ground, so the chips, the descent anchor and
 * the claim all come from the rows that carry this body's id and no others. A
 * body with no rows has no site, and therefore no plot: the descent stops at
 * whatever its pyramid's own floor is. `siteIdx` is reset only when the body
 * changed, so this is safe to call whenever either input moves.
 */
function selectSiteBody(id: string | null): void {
  // The body is usually picked before the registry fetch lands, so the first
  // non-empty list is where its default gets its first chance.
  const changed = id !== siteBodyId || bodySites.length === 0;
  siteBodyId = id;
  bodySites = id === null ? [] : SITES.filter((s) => s.body === id);
  if (changed || siteIdx >= bodySites.length) {
    siteIdx = defaultSiteIdx(bodySites, id);
  }
}
const site = (): Site | undefined => bodySites[siteIdx];
/**
 * The open site's region bake, or null on a body that has none.
 *
 * Null and not the empty string: a body with no row in `sites.json` descends
 * onto its own pyramid's floor and has no region to fetch, and the empty id
 * used to build `<base>//meta.json`, which the dev server answers with
 * index.html and every pyramid-only body read as NO TERRAIN.
 */
const siteUrl = (): string | null => {
  const s = site();
  return s ? dtmUrl(s.id) : null;
};

/** The registry's lets, which src/atlas.ts reads and writes only through this. */
export interface SiteRegistryState {
  SITES: Site[];
  sitesLoaded: boolean;
  readonly bodySites: Site[];
  readonly siteBodyId: string | null;
  siteIdx: number;
}

export const siteRegistry: SiteRegistryState = {
  get SITES() { return SITES; },
  set SITES(v) { SITES = v; },
  get sitesLoaded() { return sitesLoaded; },
  set sitesLoaded(v) { sitesLoaded = v; },
  get bodySites() { return bodySites; },
  get siteBodyId() { return siteBodyId; },
  get siteIdx() { return siteIdx; },
  set siteIdx(v) { siteIdx = v; },
};

export {
  defaultSiteIdx, normSiteQuery, selectSiteBody, site, siteBandOf, siteUrl, SITE_LIST_BANDS_ABOVE,
  spreadSyntheticSites, syntheticSiteCount, syntheticSites, syntheticSpread, zoomFloorOff,
};
